<script lang="ts" setup>
    import { onMounted, ref, reactive, computed } from 'vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import ActionButton from "../../components/ActionButton.vue"; 
    import { ICol, IRowSelection } from "../../lib/ChoiceTableHelpers";
    import { userNote } from '../../lib/ChoiceApi';
    import ModalDialog from '../../components/ModalDialog.vue'
    import TextInput from '../../components/TextInput.vue'
    import Icon from '../../components/Icon.vue'
    import { DatabaseIdTypes } from '../../Enums/ChoiceEnums';
    import { ModalSize, TableRowType, DisplayStyle, IconTypes } from "../../Enums/UiEnums";
    import { IUserNoteTableItem, IUserNoteTableResults, IUserNoteSaveNoteInputs} from '../../lib/Models/UserNoteModel';
    import '../../../css/choice.css';

    const inputFocus = ref<HTMLInputElement>();   

    const formData = reactive<{
        saving: boolean,
        tableData: IUserNoteTableItem[],
        openNote: boolean,
        noteTitle: string,
        noteText: string,
        noteId?: number,
        saveErrorMessage: string
    }>({
        saving: false,
        tableData: [],
        openNote: false,
        noteTitle: "",
        noteText: "",
        saveErrorMessage: ""
    });

    const tableColumns = ref<ICol[]>([
        { Name: '', ExtraData: { editButton: true }},
        { Name: 'Date', DataField: 'postedDate' },
        { Name: 'Note', DataField: 'note' },
        { Name: 'Created By', DataField: 'userName' },
    ]);

    const props = defineProps<{
        noteType: DatabaseIdTypes,
        patientId: number,
        noteIdentifier: number,
        noteResults: IUserNoteTableResults,
        newEnabled: boolean
    }>();

    onMounted(async () => {
        formData.tableData = props.noteResults.noteList;
    });

    const tableStyle = computed<string>(() => `font-size: 0.75rem; overflow-y: scroll; height: 220px`);
    const newButtonEnabled = computed<boolean>(() => props.newEnabled && !formData.saving);

    function NewNote() {
        formData.noteId = undefined;
        formData.noteText = "";
        formData.noteTitle = "New Note";
        formData.openNote = true;
        setTimeout(() => inputFocus.value?.focus(), 500);
    }

    async function SaveNote() {
        formData.saveErrorMessage = "";

        let post_data = <IUserNoteSaveNoteInputs> {
            patientId: props.patientId,
            recordId: props.noteIdentifier,
            noteId: formData.noteId,
            note: formData.noteText
        }

        formData.saving = true;
        const respSave = await userNote.SaveNote(post_data);
        formData.saveErrorMessage = respSave.message || "";
        formData.saving = false;

        if (respSave.passed) {
            const listResults = await userNote.GetNoteList(props.noteType, props.patientId, props.noteIdentifier);
            formData.tableData = listResults.noteList;
            formData.openNote = false;
        }
    }

    function noteRowDblClick(rowData : IRowSelection, _rowType: TableRowType, _evt: MouseEvent) {
        const localRowData = (rowData as IUserNoteTableItem & IRowSelection)
        editNote(localRowData);
    }  

    function editNote(rowData: IUserNoteTableItem & IRowSelection) {
        formData.noteId = rowData.noteId;
        formData.noteText = rowData.note;
        formData.noteTitle = "Existing Note";
        formData.openNote = true;
    }

</script>

<template>
    <div class="row">
        <div class="col label-isalus-small mt-3">Notes</div>
        <div class="col-auto">
            <button type="button" class="btn btn-primary label-isalus-small" :disabled="!newButtonEnabled" title="Create a new note." @click="NewNote()">
                <Icon :icon="IconTypes.Add" :displayStyle="DisplayStyle.Light"></Icon>
            </button>
        </div>
    </div>
    <div class="row mt-1">
        <div class="col">
            <p :style="tableStyle">
                <ChoiceTable 
                    ref="userNotesTable"
                    :columns="tableColumns" 
                    :data="formData.tableData" 
                    :show-filters="false" 
                    :allowSorting="true"
                    :key-value-data-member="'Col3Val'"
                    @rowdblclick="noteRowDblClick">Search
                    <template #cell="{col, row, value}">
                        <template v-if="col.ExtraData?.editButton">
                            <button class="btn btn-primary btn-sm label-isalus-small" @click="editNote(row as IUserNoteTableItem & IRowSelection)" title="Edit note">
                                <Icon :icon="IconTypes.Edit" :displayStyle="DisplayStyle.Light"/>
                            </button>
                        </template>
                    </template>
                </ChoiceTable>
            </p>
        </div>
    </div>

    <ModalDialog :size=ModalSize.Small v-model:show="formData.openNote" :closeOnClickOutside="false">
        <template v-slot:title>{{formData.noteTitle}}</template>
        <div class="row">
            <div class="col">
                <TextInput
                    ref="inputFocus"
                    v-model:text-value="formData.noteText" 
                    :maxLength="5000"
                    :cssFramework = "'vanilla'"
                    :rows=3>
                </TextInput>
            </div>
        </div> 
        <template #footer>
            <div v-if="formData.saveErrorMessage" class="text-danger">{{formData.saveErrorMessage}}</div>
            <ActionButton
                baseText = "Save"
                spinText = "Saving"
                :executing="formData.saving"    
                :disabled="formData.saving"   
                :clickEvent="SaveNote" 
                :toolTip="'Save the note.'"
                :executeInit="false"
                :width="120"
                :baseClass="'btn btn-primary label-isalus-small'">
            </ActionButton> 
        </template>
    </ModalDialog>

</template>

